import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Box,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import ModelsPage from './pages/ModelsPage';
import TrainingPage from './pages/TrainingPage';
import CompetitionPage from './pages/CompetitionPage';
import PortfolioPage from './pages/PortfolioPage';
import IntroPage from './pages/IntroPage';
import ContactPage from './pages/ContactPage';
import DemoPage from './pages/DemoPage';
import PricingPage from './pages/PricingPage';
import PricingAdminPage from './pages/PricingAdminPage';
import AuthPage from './pages/AuthPage';
import RoadmapPage from './pages/RoadmapPage';
import { theme } from './theme';
import { getVisitorCount, getCurrentUser, subscribeEmail } from './api/rlApi';
import SplashIntro from './components/SplashIntro';

function App() {
  const tabForPath = (path) => {
    if (path.startsWith('/training')) return 'training';
    if (path.startsWith('/models')) return 'models';
    if (path.startsWith('/competition')) return 'competition';
    if (path.startsWith('/portfolio')) return 'portfolio';
    if (path.startsWith('/pricing')) return 'pricing';
    if (path.startsWith('/auth')) return 'auth';
    if (path.startsWith('/demo')) return 'demo';
    if (path.startsWith('/contact')) return 'contact';
    if (path.startsWith('/roadmap')) return 'roadmap';
    return 'intro';
  };

  const [value, setValue] = React.useState(() => tabForPath(window.location?.pathname || '/'));
  const [visitorCount, setVisitorCount] = React.useState(null);
  const [visitorError, setVisitorError] = React.useState(null);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [visitorLoaded, setVisitorLoaded] = React.useState(false);
  const [userLoaded, setUserLoaded] = React.useState(false);
  const [isBootstrapReady, setIsBootstrapReady] = React.useState(false);
  const [splashDismissed, setSplashDismissed] = React.useState(false);
  const [emailSubmitted, setEmailSubmitted] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const handleLogoutNav = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('auth_token');
    }
    setCurrentUser(null);
    setValue('auth');
    window.dispatchEvent(new Event('auth-updated'));
  };
  const loadUser = React.useCallback(async () => {
    try {
      const res = await getCurrentUser();
      setCurrentUser(res.user);
    } catch (err) {
      setCurrentUser(null);
    } finally {
      setUserLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    document.title = 'Future AI Alliance - Quant Terminal';
  }, []);

  React.useEffect(() => {
    let active = true;
    const loadVisitors = async () => {
      try {
        const res = await getVisitorCount();
        if (!active) return;
        setVisitorCount(res?.count ?? null);
        setVisitorError(null);
      } catch (err) {
        if (!active) return;
        setVisitorError('Unable to load visitors');
      } finally {
        if (active) setVisitorLoaded(true);
      }
    };
    loadVisitors();
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    let active = true;
    const run = async () => {
      if (!active) return;
      await loadUser();
    };
    run();
    const handleAuthUpdated = () => loadUser();
    window.addEventListener('auth-updated', handleAuthUpdated);
    window.addEventListener('storage', handleAuthUpdated);
    return () => {
      active = false;
      window.removeEventListener('auth-updated', handleAuthUpdated);
      window.removeEventListener('storage', handleAuthUpdated);
    };
  }, [loadUser]);

  React.useEffect(() => {
    if (visitorLoaded && userLoaded) {
      setIsBootstrapReady(true);
    }
  }, [visitorLoaded, userLoaded]);

  const navItems = React.useMemo(() => {
    if (currentUser) {
      return [
        { value: 'auth', label: '⚙ Settings', to: '/auth#login' },
        { value: 'intro', label: '🏁 Intro', to: '/' },
        { value: 'training', label: '🎯 Training', to: '/training' },
        { value: 'models', label: '🤖 Models', to: '/models' },
        { value: 'competition', label: '🏆 Competition', to: '/competition' },
        { value: 'portfolio', label: '📊 Portfolio', to: '/portfolio' },
        { value: 'contact', label: '📮 Contact', to: '/contact' },
      ];
    }
    return [
      { value: 'intro', label: '🏁 Intro', to: '/' },
      { value: 'demo', label: '📅 Demo', to: '/demo' },
      { value: 'pricing', label: '💳 Pricing', to: '/pricing' },
      { value: 'contact', label: '📮 Contact', to: '/contact' },
      { value: 'auth', label: '⚙ Settings', to: '/auth' },
    ];
  }, [currentUser]);

  const safeValue = React.useMemo(() => {
    const has = navItems.some((item) => item.value === value);
    if (has) return value;
    return navItems[0]?.value ?? false;
  }, [navItems, value]);

  const handleChange = (event, newValue) => {
    if (!newValue) return;
    setValue(newValue);
  };

  const handleEmailSubmit = async (email) => {
    try {
      await subscribeEmail(email);
      setEmailSubmitted(true);
      setToast({
        open: true,
        message: 'Email received — thanks for joining!',
        severity: 'success',
      });
    } catch (err) {
      setToast({
        open: true,
        message: 'Unable to submit email right now.',
        severity: 'error',
      });
      throw err;
    }
  };

  const handleSplashClose = () => {
    setSplashDismissed(true);
  };

  const showSplash = !isBootstrapReady || (!emailSubmitted && !splashDismissed);

  const ProtectedRoute = ({ element }) => {
    if (!currentUser) {
      return <Navigate to="/auth" replace />;
    }
    return element;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {showSplash && (
        <SplashIntro
          appReady={isBootstrapReady}
          onSubmitEmail={handleEmailSubmit}
          onClose={handleSplashClose}
          bannerVideo="/splash.mp4"
        />
      )}
      <Router>
        <AppBar position="static">
          <Toolbar sx={{ justifyContent: 'center' }}>
            <Typography variant="h6" component="div" align="center">
              Future AI Alliance - Quant Terminal
            </Typography>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 2,
          }}
        >
          <Tabs
            value={safeValue}
            onChange={handleChange}
            aria-label="navigation tabs"
            variant="standard"
            centered
            sx={{ maxWidth: 1200, mx: 'auto' }}
          >
            {navItems.map((item) => (
              <Tab
                key={item.value}
                value={item.value}
                label={item.label}
                component={Link}
                to={item.to}
                onClick={() => setValue(item.value)}
              />
            ))}
          </Tabs>
          {currentUser && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleLogoutNav}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Logout
            </Button>
          )}
        </Box>

        <Box sx={{ pb: 8 }}>
          <Routes>
            <Route path="/" element={<IntroPage />} />
            <Route path="/models" element={<ProtectedRoute element={<ModelsPage />} />} />
            <Route path="/training" element={<ProtectedRoute element={<TrainingPage />} />} />
            <Route path="/competition" element={<ProtectedRoute element={<CompetitionPage />} />} />
            <Route path="/portfolio" element={<ProtectedRoute element={<PortfolioPage />} />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing/admin" element={<PricingAdminPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
          </Routes>
        </Box>

        <Box
          component="footer"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            py: 1,
            px: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            zIndex: (theme) => theme.zIndex.appBar - 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            👥 Visitors: {visitorCount ?? '—'}
          </Typography>
          {visitorError && (
            <Typography variant="body2" color="error">
              {visitorError}
            </Typography>
          )}
        </Box>
      </Router>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;